import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AccountsService, SidePanelService } from '@lib/services';
import { NgxScannerQrcodeModule, LOAD_WASM, ScannerQRCodeConfig, NgxScannerQrcodeComponent, ScannerQRCodeResult } from 'ngx-scanner-qrcode';
import { AngularMaterialModule } from 'src/app/material.module';
import { ConfirmationComponent } from '../confirmation/confirmation.component';
import { IVoucherGenerationResponse, IVoucherListIndex, VoucherOperationType } from '@lib/interfaces/card-less.interface';
import { finalize, map } from 'rxjs';
import { ApiResponseError } from '@lib/interfaces';
import { CardLessService } from '@lib/services/card-less/card-less.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from '@lib/components';
import { SpinnerComponent } from '../../../../lib/components/spinner/spinner.component';

LOAD_WASM().subscribe();

@Component({
    selector: 'app-qr-scanner',
    templateUrl: './qr-scanner.component.html',
    styleUrls: ['./qr-scanner.component.scss'],
    standalone: true,
    imports: [NgxScannerQrcodeModule, AngularMaterialModule, SpinnerComponent],
    providers: [SidePanelService],
})
export class QrScannerComponent extends BaseComponent implements AfterViewInit {
    public cameraConfig: ScannerQRCodeConfig = {
        constraints: {
            video: {
                width: window.innerWidth,
            },
        },
        isBeep: false,
    };
    loading = false;

    @ViewChild('scanner') scannerComponent!: NgxScannerQrcodeComponent;

    constructor(public dialogRef: MatDialogRef<QrScannerComponent>, private sidePanelService: SidePanelService, private cardLessService: CardLessService, private snackBarService: MatSnackBar, private accountService: AccountsService) {
        super();
    }

    ngAfterViewInit(): void {
        this.scannerComponent.isReady.subscribe((res: any) => {
            this.handle(this.scannerComponent, 'start');
        });
    }

    public override ngOnDestroy(): void {
        this.scannerComponent.pause() && this.scannerComponent.stop();
    }

    public onEvent(e: ScannerQRCodeResult[], scanner?: any): void {
        // validation login for QR code
        if (e[0].typeName === 'ZBAR_QRCODE' && this.loading === false) {
            const voucherData: IVoucherGenerationResponse = JSON.parse(e[0].value);
            e && scanner && scanner.pause() && scanner.stop();
            this.loading = true;

            this.cardLessService
                .voucherList({
                    voucher_code: voucherData.voucher_code.trim(),
                    operation_code: VoucherOperationType.INWARD,
                    limit: 1,
                    after: '',
                })
                .pipe(
                    map((response) => {
                        if (!response.data || response.data.length === 0) {
                            throw new Error('Voucher not found');
                        }

                        const voucher: IVoucherListIndex = response.data[0];
                        const expiryDate = new Date(voucher.expiry_time);

                        if (voucher.voucher_status === 'CANCELED') throw new Error('Voucher is canceled');
                        if (voucher.voucher_status === 'REDEEMED') throw new Error('Voucher is already redeemed');
                        if (new Date() > expiryDate) throw new Error('Voucher has expired');
                        if (!this.accountService.agentsAccounts().get(voucher.currency)) throw new Error(`Agent does not have ${voucher.currency} currency wallet. Please contact support`);
                        return response;
                    }),
                    finalize(() => {
                        this.loading = false;
                    }),
                )
                .subscribe({
                    next: ({ data: data }) => {
                        this.dialogRef.close();
                        const dialogRef = this.sidePanelService.open(ConfirmationComponent);
                        dialogRef.componentInstance.screen = 'cash-in';
                        dialogRef.componentInstance.voucherInfo = data[0];
                    },
                    error: (error: ApiResponseError | Error) => {
                        this.createApiErrorSnackBar(this.snackBarService, error);
                        this.restartScanner();
                    },
                });
        }
    }

    public handle(scanner: any, fn: string): void {
        const playDeviceFacingBack = (devices: any[]) => {
            // front camera or back camera check here!
            const device = devices.find((f) => /back|rear|environment/gi.test(f.label)); // Default Back Facing Camera
            scanner.playDevice(device ? device.deviceId : devices[0].deviceId);
        };

        if (fn === 'start') {
            scanner[fn](playDeviceFacingBack).subscribe((r: any) => console.log(fn, r), alert);
        } else {
            scanner[fn]().subscribe((r: any) => console.log(fn, r), alert);
        }
    }

    private restartScanner(): void {
        setTimeout(() => {
            this.handle(this.scannerComponent, 'start');
        });
    }
}
